import { Box, Divider, MenuItem, TextField, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { getISODate } from "../../utils/date";
import { MonthSelect, MonthSelectButtonsPosition } from "../fields/MonthSelect";
import WorkoutActivityCalendar from "./WorkoutActivityCalendar";
import {
  BodypartType,
  ExerciseAssetDto,
  UserExerciseDto,
} from "@growth-machine-llc/stridist-api-client";
import { BodypartTypeLabels } from "../exercise-library/constants";
import { JourneyAnchors } from "../../constants";
import clsx from "clsx";
import dayjs from "dayjs";
import Chip from "@mui/material/Chip";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: 20,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    gap: theme.spacing(2),
    fontSize: 14,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
  },
  calendarHeader: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    justifyContent: "space-between",
  },
  calendarControlButtons: {
    width: 24,
    height: 24,
    margin: 0,
    color: theme.palette.text.primary,
  },
  calendarLabel: {
    fontSize: 14,
    fontWeight: 600,
    marginLeft: 0,
  },

  primaryText: {
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  categoryChip: {
    marginLeft: theme.spacing(2),
    height: 20,
    fontSize: 12,
    width: 85,
  },
  categoryHeader: {
    padding: theme.spacing(0, 1, 0.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  categoryHeaderText: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
  },
}));

interface WorkoutActivityOverviewProps {
  clientId: number;
  exerciseAsset: ExerciseAssetDto;
  onExerciseSelect?: (exercise: UserExerciseDto) => void;
  endDate?: dayjs.Dayjs;
}

const WorkoutActivityOverview: React.FC<WorkoutActivityOverviewProps> = ({
  clientId,
  exerciseAsset,
  onExerciseSelect,
  endDate,
}) => {
  const s = useStyles();
  const [date, setDate] = React.useState(
    endDate?.toISOString() || getISODate(),
  );

  // Change calendar date simultaneously with general date navigation
  React.useEffect(() => {
    if (endDate) {
      setDate(endDate?.toISOString());
    }
  }, [endDate]);

  const exerciseAssetBodyparts = exerciseAsset?.bodyparts || [];
  const otherBodyparts = Object.values(BodypartType).filter(
    (bp) => bp !== BodypartType.NONE && !exerciseAssetBodyparts.includes(bp),
  );

  const [bodypart, setBodypart] = React.useState<BodypartType>(
    exerciseAssetBodyparts[0] || BodypartType.BICEPS,
  );

  const showBodypartsTypeLabel = exerciseAssetBodyparts.length > 0;

  const BodypartLabelBox = (title: string) => (
    <>
      <Box className={s.categoryHeader}>
        <Typography className={s.categoryHeaderText}>{title}</Typography>
      </Box>
      <Divider />
    </>
  );

  return (
    <Box className={clsx(s.root, JourneyAnchors.ACTIVITIES_BODYPART_CALENDAR)}>
      <Box
        display={"flex"}
        justifyContent="space-between"
        alignItems="center"
        gap={1}
      >
        <Typography className={s.title}>Activity Calendar</Typography>
        <TextField
          select
          variant="outlined"
          size="small"
          value={bodypart}
          onChange={(e) => setBodypart(e.target.value as BodypartType)}
          sx={{
            minWidth: 80,
            ".MuiOutlinedInput-root": {
              fontWeight: 500,
              borderRadius: 2,
              "& fieldset": {
                borderRadius: 2,
              },
            },
          }}
          slotProps={{
            input: {
              sx: { height: 40 },
            },
            select: {
              renderValue: (selected) => (
                <Typography className={s.primaryText}>
                  {BodypartTypeLabels[selected as BodypartType]}
                </Typography>
              ),
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                  },
                },
                sx: { zIndex: 99999 },
              },
            },
          }}
        >
          {showBodypartsTypeLabel &&
            BodypartLabelBox(`${exerciseAsset.name}'s bodyparts`)}

          {exerciseAssetBodyparts.map((bp, idx) => (
            <MenuItem key={bp} value={bp}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Typography className={s.primaryText}>
                  {BodypartTypeLabels[bp]}
                </Typography>

                <Chip
                  size="small"
                  label={idx === 0 ? "Primary" : "Secondary"}
                  color={idx === 0 ? "primary" : "default"}
                  className={s.categoryChip}
                />
              </Box>
            </MenuItem>
          ))}
          {exerciseAssetBodyparts.length > 0 && <Divider />}
          {showBodypartsTypeLabel && BodypartLabelBox("Other bodyparts")}
          {otherBodyparts.map((bp) => (
            <MenuItem key={bp} value={bp}>
              <Typography className={s.primaryText}>
                {BodypartTypeLabels[bp]}
              </Typography>
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box display={"flex"} flexDirection="column" gap={2}>
        <MonthSelect
          onSelect={setDate}
          date={date}
          buttonsPosition={MonthSelectButtonsPosition.RIGHT}
          classes={{
            label: s.calendarLabel,
            navigationButtons: s.calendarControlButtons,
            container: s.calendarHeader,
          }}
        />
        <Box>
          <WorkoutActivityCalendar
            clientId={clientId}
            exerciseAssetId={exerciseAsset.id}
            bodypart={bodypart}
            date={date}
            onExerciseSelect={onExerciseSelect}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default WorkoutActivityOverview;
